<template>
  <div class="advertising">
    <div class="advertising__summary">
      <div>
        <div class="advertising__summary-title">广告位内容</div>
        <div class="advertising__summary-desc">按展示顺序维护广告行和广告条目，支持轮播和均匀分布两种展示方式。</div>
      </div>
      <HpsfirButton class="advertising__add-btn" @click="() => addDialogRef?.showDialog()">
        <el-icon><Plus /></el-icon>
        添加一行
      </HpsfirButton>
    </div>

    <div v-if="loading" class="advertising__empty advertising__empty--loading">
      <div class="advertising__empty-icon">...</div>
      <div class="advertising__empty-title">广告位加载中</div>
      <div class="advertising__empty-desc">正在获取广告位数据，请稍候。</div>
    </div>

    <div v-else-if="!advertisingRowDatas.length" class="advertising__empty">
      <div class="advertising__empty-icon">AD</div>
      <div class="advertising__empty-title">暂无广告行</div>
      <div class="advertising__empty-desc">点击“添加一行”创建轮播或均匀分布广告位。</div>
    </div>

    <div v-else class="advertising__rows">
      <AdvertisingRow
        v-for="(advertisingRow, inx) in advertisingRowDatas"
        :key="advertisingRow.id"
        :row-data="advertisingRow"
        :is-first="inx === 0"
        :is-last="inx === advertisingRowDatas.length - 1"
        @change="() => init()"
      />
    </div>

    <HpsfirProForm :is-dialog="true" ref="addDialogRef" title="添加一行" :items="items" @ok="handleAdd"/>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import AdvertisingRow from './AdvertisingRow.vue';
import {
  useBaseAsync,
  type HpsfirProFormItem,
} from 'hpsfir-managebase-ui';

import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';

const loading = ref(false);

const props = defineProps<{
  position: string;
}>();

const addDialogRef = ref<{ reset?(): void; setOneFormState?(name: string, value: unknown): void }>();

const rowOp: {
    value: any;
    label: string;
}[] = [
  {
    label: '轮播',
    value: 'carousel',
  },
  {
    label: '均匀分布',
    value: 'distribute',
  },
];

const items: HpsfirProFormItem[] = [
  {
    type: 'select',
    label: '类型',
    name: 'type',
    options: rowOp,
    rule: [{ required: true }],
  },
];

const advertisingRowDatas = ref<any[]>([]);

function withTimeout<T>(promise: Promise<T>, message = '广告位数据加载超时') {
  return Promise.race([
    promise,
    new Promise<T>((resolve, reject) => {
      void resolve;
      window.setTimeout(() => reject(new Error(message)), 15000);
    }),
  ]);
}

const init = useBaseAsync(async() => {
  try {
    loading.value = true;
    const res = await withTimeout(useApi().advertisingApi.listAdvertisingRowadvertising({
      conditions: {
        position: props.position,
      },
    }));
    advertisingRowDatas.value = res?.data?.data || [];
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '广告位数据加载失败');
  } finally {
    loading.value = false;
  }
});

const handleAdd = useBaseAsync(async(data: any) => {
  try {
    await withTimeout(useApi().advertisingApi.addAdvertisingRowadvertising({
      advertisingRowAdminVo: {
        position: props.position,
        type: data.type,
        sequence: advertisingRowDatas.value.length + 1,
      },
    }), '添加广告行超时');
    ElMessage.success('添加成功');
    init();
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '添加广告行失败');
  }
});

init();
</script>

<style lang="scss">
.advertising {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 220px;

  &__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 72%, transparent);
    border-radius: 18px;
    background: color-mix(in srgb, var(--card, #fff) 94%, transparent);
    box-shadow: 0 14px 30px -24px rgba(15, 23, 42, 0.35);
  }

  &__summary-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--foreground, #111827);
  }

  &__summary-desc {
    margin-top: 4px;
    font-size: 13px;
    color: var(--muted-foreground, #6b7280);
  }

  &__add-btn {
    flex-shrink: 0;
    gap: 6px;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__empty {
    display: flex;
    min-height: 180px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 28px;
    border: 1px dashed color-mix(in srgb, var(--border, #d1d5db) 80%, transparent);
    border-radius: 20px;
    background: color-mix(in srgb, var(--muted, #f3f4f6) 36%, transparent);
    text-align: center;
  }

  &__empty-icon {
    display: flex;
    width: 52px;
    height: 52px;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    background: var(--primary, #111827);
    color: var(--primary-foreground, #fff);
    font-weight: 800;
    letter-spacing: 0.08em;
  }

  &__empty-title {
    font-weight: 700;
    color: var(--foreground, #111827);
  }

  &__empty-desc {
    font-size: 13px;
    color: var(--muted-foreground, #6b7280);
  }

  &__empty--loading &__empty-icon {
    animation: advertising-loading-pulse 1.2s ease-in-out infinite;
  }
}

@keyframes advertising-loading-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}

@media (max-width: 767px) {
  .advertising {
    gap: 12px;

    &__summary {
      align-items: stretch;
      flex-direction: column;
      padding: 14px;
      border-radius: 16px;
    }

    &__add-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
